// The table of both waiting rooms (FriendsWaitingRoom, OnlineWaitingRoom): seen from above, in the
// player's own felt (the colour grid changes it live), the player at the bottom and a seat per other
// player. A spinner turns on each free seat until somebody sits there; then the avatar pops in with a
// gold ripple. The middle of the felt shows how many are seated, then the countdown of an online room.
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Bot, Check, Crown, Hash, Lightbulb, Loader2, Palette, Play, Shield, UserRound, Zap } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { TableColorGrid } from "../TableColorPicker";
import { RankEmblem } from "../rank/RankEmblem";
import { PlayerCardDialog } from "../players/PlayerCardDialog";
import { t } from "../../lib/i18n";
import { tableTheme, useMyTableTheme } from "../../lib/tableThemes";
import { sound } from "../../lib/feedback";
import { seatsFrom } from "../../lib/seating";

// Where the other players sit, clockwise from the player's own seat at the bottom. Three players: one
// on each side of the table (owner's request; they sat at its top corners).
const SEATS = { 2: ["top"], 3: ["left", "right"], 4: ["left", "top", "right"] };
// The top of the table goes up by this much when nobody sits at the top (3 players): 36 px of inset → 10.
const TOP_GAP = 26;
// On a plain wrapper: framer-motion's transform on the seat itself would replace the translate.
const SEAT_POS = {
  top: "left-1/2 top-0 -translate-x-1/2",
  left: "left-0 top-1/2 -translate-y-1/2",
  right: "right-0 top-1/2 -translate-y-1/2",
  bottom: "left-1/2 bottom-0 -translate-x-1/2",
};

// The felt, from the colour's values (not `.rami-mat`: its inset shadow is sized for the whole game
// table and would turn this small one black).
const feltStyle = (id) => {
  const th = tableTheme(id);
  return {
    background:
      `radial-gradient(ellipse at 50% 45%, rgba(${th.glow}, ${th.glowAlpha ?? 0.35}) 0%, rgba(${th.mid}, 0.95) 58%, rgb(${th.edge}) 100%),` +
      " repeating-linear-gradient(45deg, rgba(255,255,255,0.025) 0 2px, transparent 2px 8px)",
    boxShadow: `inset 0 0 36px rgba(0, 0, 0, 0.55), inset 0 0 16px rgba(${th.glow}, 0.18)`,
  };
};
// The wooden rim, with a thin line of gold.
const RIM = "linear-gradient(160deg, #4a3020 0%, #24160d 45%, #3b2618 70%, #160d07 100%)";

function TakenSeat({ member, isMe, isHost, onOpen }) {
  // Your own seat has no word under the name (owner's request): the gold ring says it is you.
  const status = isMe
    ? null
    : member.watching_ad
    ? t("opp.watchingAd")
    : member.is_bot
    ? t("room.role.bot")
    : isHost
    ? t("room.role.host")
    : t("waitingTable.ready");
  // Another human's seat opens their card (PlayerCardDialog).
  const openable = onOpen && !isMe && !member.is_bot;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.4, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.6, transition: { duration: 0.15 } }}
      transition={{ type: "spring", stiffness: 420, damping: 22 }}
      className={`flex w-full flex-col items-center gap-1 ${openable ? "cursor-pointer" : ""}`}
      data-testid={`member-${member.user_id}`}
      {...(openable ? {
        role: "button",
        tabIndex: 0,
        title: t("players.open", { name: member.name }),
        onClick: () => onOpen(member.user_id),
        onKeyDown: (e) => { if (e.key === "Enter") onOpen(member.user_id); },
      } : {})}
    >
      <div className="relative">
        {/* One gold ripple when the player sits down (not for those already there on opening). */}
        <motion.span
          aria-hidden="true"
          className="absolute inset-0 rounded-full ring-2 ring-amber-300 motion-reduce:hidden"
          initial={{ scale: 1, opacity: 0.9 }}
          animate={{ scale: 1.9, opacity: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />
        <Avatar
          className={`h-9 w-9 ring-2 ring-offset-2 ring-offset-slate-950 ${
            isMe ? "ring-amber-400" : member.is_bot ? "ring-emerald-400/60" : "ring-white/30"
          }`}
        >
          {member.is_bot ? (
            <>
              {/* The medal the server drew for it (game_service.bot_picture); the robot until it loads. */}
              {member.picture && <AvatarImage src={member.picture} />}
              <AvatarFallback className="bg-emerald-900/70 text-emerald-300">
                <Bot className="h-3.5 w-3.5" />
              </AvatarFallback>
            </>
          ) : (
            <>
              <AvatarImage src={member.picture} />
              <AvatarFallback className="bg-slate-800 text-[13px] font-semibold text-amber-300">
                {member.name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </>
          )}
        </Avatar>
        {isHost && (
          <span
            title={t("room.role.host")}
            className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-slate-950 ring-1 ring-amber-400/60"
          >
            <Crown className="h-2.5 w-2.5 text-amber-400" />
          </span>
        )}
        {/* The player's rank (lib/ranks.js) on the avatar's other lower corner. */}
        {!member.is_bot && member.rank && (
          <RankEmblem
            rank={member.rank}
            size={16}
            className="absolute -bottom-1 -left-1.5 drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]"
          />
        )}
        {!member.is_bot && !isMe && (
          <span
            className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-slate-950 ${
              member.watching_ad ? "bg-amber-400" : "bg-emerald-400"
            }`}
          />
        )}
      </div>
      <span dir="auto" className="max-w-full truncate text-[11px] font-semibold leading-tight text-white">
        {member.name}
      </span>
      {status && (
        <span
          className={`-mt-1 text-[10px] leading-tight ${
            member.watching_ad || isHost ? "text-amber-300" : "text-emerald-300/80"
          }`}
          title={member.watching_ad ? t("opp.watchingAdTitle") : undefined}
        >
          {status}
        </span>
      )}
    </motion.div>
  );
}

// A free seat; in a rematch room, the player of the previous game it is kept for (`awaited`).
function FreeSeat({ label, awaited }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.6, transition: { duration: 0.15 } }}
      className="flex w-full flex-col items-center gap-1"
      data-testid="seat-free"
    >
      <div className="relative flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-white/20 bg-slate-950/75">
        <span
          aria-hidden="true"
          className="absolute -inset-[3px] animate-spin rounded-full border-2 border-transparent border-r-amber-400/30 border-t-amber-400 [animation-duration:1.1s]"
        />
        {awaited ? (
          <span className="text-[13px] font-semibold text-slate-400">{awaited.name?.[0]?.toUpperCase()}</span>
        ) : (
          <UserRound className="h-3.5 w-3.5 text-slate-600" />
        )}
      </div>
      {awaited && (
        <span dir="auto" className="max-w-full truncate text-[11px] font-semibold leading-tight text-slate-300">
          {awaited.name}
        </span>
      )}
      {/* An invited player's seat says « Invité » or « A refusé » (`awaited.label`). */}
      <span className={`leading-tight ${awaited?.declined ? "text-rose-300/90" : "text-slate-300/80"} ${awaited ? "-mt-1 text-[10px]" : "text-[10px]"}`}>
        {awaited?.label || label}
      </span>
    </motion.div>
  );
}

function Seat({ position, member, isMe, isHost, freeLabel, awaited, style, onOpen }) {
  return (
    <div className={`absolute ${SEAT_POS[position]} w-[76px]`} style={style}>
      {/* initial={false}: the players already seated when the page opens do not pop in. */}
      <AnimatePresence mode="wait" initial={false}>
        {member ? (
          <TakenSeat key={member.user_id} member={member} isMe={isMe} isHost={isHost} onOpen={onOpen} />
        ) : (
          <FreeSeat key={`free-${awaited?.user_id || ""}-${awaited?.label || ""}`} label={freeLabel} awaited={awaited} />
        )}
      </AnimatePresence>
    </div>
  );
}

// The middle of the felt: how many are seated, then the countdown (online rooms), or a spinner
// while the game starts.
function TableCentre({ seated, total, secsLeft, starting }) {
  const full = seated >= total;
  // A friends' room may start before it is full.
  const view = starting ? "starting" : !full ? "count" : secsLeft > 0 ? "countdown" : "full";
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      {view === "count" && (
        <>
          <span aria-hidden="true" className="absolute h-20 w-20 animate-ping rounded-full border border-amber-200/20 [animation-duration:2.4s] motion-reduce:hidden" />
          <span aria-hidden="true" className="absolute h-14 w-14 rounded-full border border-amber-200/10" />
        </>
      )}
      <AnimatePresence mode="wait" initial={false}>
        {view === "count" || view === "full" ? (
          <motion.div
            key={view}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="relative flex flex-col items-center"
          >
            <div dir="ltr" className="relative font-display text-[30px] leading-none text-amber-200 tabular-nums drop-shadow">
              <motion.span key={seated} initial={{ y: -6, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="inline-block">
                {seated}
              </motion.span>
              <span className="text-[20px] text-amber-200/45">/{total}</span>
              {/* Beside the count, not above it: the low table has no room for one more line. */}
              {view === "full" && (
                <span className="absolute -right-7 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-400/20 ring-1 ring-emerald-300/50">
                  <Check className="h-3 w-3 text-emerald-300" strokeWidth={3} />
                </span>
              )}
            </div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-50/60">
              {t("waitingTable.atTable")}
            </div>
          </motion.div>
        ) : view === "countdown" ? (
          <motion.div
            key="countdown"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="relative flex flex-col items-center"
          >
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-50/70">
              {t("waitingTable.startsIn")}
            </div>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div
                key={secsLeft}
                initial={{ opacity: 0, scale: 1.7 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                transition={{ duration: 0.3 }}
                className="font-display text-[34px] leading-none text-amber-300 tabular-nums drop-shadow-[0_0_14px_rgba(251,191,36,0.45)]"
              >
                {secsLeft}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.div key="starting" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
            <Loader2 className="h-8 w-8 animate-spin text-amber-300" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * The table and its seats. `showHost`: a crown on the host's seat (friends' rooms; strangers have
 * no use for it). `awaited`: in a rematch room, the players the free seats are kept for.
 */
export function WaitingTable({ room, user, freeLabel, showHost = false, awaited = [], secsLeft = null, starting = false }) {
  const themeId = useMyTableTheme();
  const [cardOf, setCardOf] = useState(null);
  const total = room.num_players;
  const seated = Math.min(room.members.length, total);
  // Me at the bottom, the others clockwise from my left in the order of play (lib/seating.js): the seats
  // are the order the room was filled in, which the game keeps. A free seat is where the next to come sits.
  const me = room.members.find((m) => m.user_id === user.user_id)
    || { user_id: user.user_id, name: user.name, picture: user.picture };
  const others = seatsFrom(room.members, user.user_id, total);
  // A rematch room keeps its free seats for the previous game's players, in order.
  const awaitedAt = others.map((m, i) => (m ? undefined : awaited[others.slice(0, i).filter((x) => !x).length]));
  const positions = SEATS[total] || SEATS[4];
  const hostOf = (m) => showHost && m?.user_id === room.host_user_id;
  // No seat at the top (3 players): the table goes up into that room, so the line above it is not left
  // far away (owner's request), and the side seats stay at its middle (TOP_GAP px higher).
  const noTop = !positions.includes("top");
  const sideStyle = (pos) => (noTop && (pos === "left" || pos === "right") ? { top: `calc(50% - ${TOP_GAP / 2}px)` } : undefined);

  return (
    // The table never mirrors, like the game board.
    <div
      dir="ltr"
      className={`relative mx-auto w-full max-w-[400px] ${noTop ? "h-[178px] sm:h-[194px]" : "h-[204px] sm:h-[220px]"}`}
    >
      <div
        className={`absolute inset-x-9 bottom-9 rounded-full p-[5px] shadow-[0_18px_40px_rgba(0,0,0,0.55)] ${noTop ? "top-2.5" : "top-9"}`}
        style={{ background: RIM }}
      >
        <div className="relative h-full w-full overflow-hidden rounded-full ring-1 ring-amber-300/30">
          <AnimatePresence initial={false}>
            <motion.div
              key={themeId}
              className="absolute inset-0"
              style={feltStyle(themeId)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
            />
          </AnimatePresence>
          <TableCentre seated={seated} total={total} secsLeft={secsLeft} starting={starting} />
        </div>
      </div>
      {positions.map((pos, i) => (
        <Seat
          key={pos}
          position={pos}
          style={sideStyle(pos)}
          member={others[i]}
          isHost={hostOf(others[i])}
          freeLabel={freeLabel}
          awaited={awaitedAt[i]}
          onOpen={setCardOf}
        />
      ))}
      <Seat position="bottom" member={me} isMe isHost={hostOf(me)} />
      <PlayerCardDialog userId={cardOf} onClose={() => setCardOf(null)} inviteBlocked={t("players.sameTable")} />
    </div>
  );
}

/** A chime when somebody sits down (a player or a bot), a brighter one when the table is full. */
export function useSeatChime(members, total, userId) {
  const seenRef = useRef(null);
  useEffect(() => {
    const ids = members.map((m) => m.user_id);
    const before = seenRef.current;
    seenRef.current = ids;
    if (!before) return;
    if (!ids.some((id) => id !== userId && !before.includes(id))) return;
    if (ids.length >= total) sound.tableFull();
    else sound.seat();
  }, [members, total, userId]);
}

/** The colours of the table: each player picks their own (kept on their device). */
export function TableColorSection() {
  return (
    <div data-testid="table-theme">
      <div className="mb-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        <Palette className="h-2.5 w-2.5" /> {t("room.table.title")}
      </div>
      <TableColorGrid />
    </div>
  );
}

/** The rules, at the top of the room: three equal chips that shrink on narrow phones (the label
 *  gives way, never the value). */
export function RulesChips({ room }) {
  return (
    <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
      {[
        { icon: Hash, label: t("common.threshold"), value: `${room.threshold_x}` },
        { icon: Shield, label: t("common.elim"), value: `${room.elim_score_y}` },
        { icon: Zap, label: t("common.jokers"), value: `${room.num_jokers}` },
      ].map(({ icon: Icon, label, value }) => (
        <div key={label} className="min-w-0 flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 rounded-lg bg-white/[0.03] border border-white/[0.06]">
          <Icon className="h-3.5 w-3.5 text-amber-400/60 shrink-0" />
          <span className="min-w-0 truncate text-[11px] text-slate-500">{label}</span>
          <span className="ms-auto shrink-0 text-[12px] font-bold text-amber-300 tabular-nums">{value}</span>
        </div>
      ))}
    </div>
  );
}

const TIP_EVERY_MS = 7000;
// « Le savais-tu ? » tips (`waitingTable.tips.*`), in the order shown. A tip quoting a penalty gets the
// room's own value, and is left out when the room turned it off; same for the run without a Joker.
const TIPS = [
  { key: "joker" },
  { key: "jokersApart" },
  { key: "noPose" },
  { key: "ace" },
  { key: "usefulDiscard", penalty: "useful_discard_penalty" },
  { key: "jokerHand", penalty: "joker_in_hand_penalty" },
  { key: "pureSeq", rule: "require_pure_sequence" },
  { key: "threshold" },
  { key: "takeDiscard" },
  { key: "addToTable" },
  { key: "continuer", penalty: "continuer_penalty" },
  { key: "botReplace" },
];

/** "Did you know?": the tips take turns in one grid cell, so the card keeps the height of the
 *  longest and nothing below it moves. A tap shows the next one. */
export function Tips({ room }) {
  const tips = TIPS
    .filter((tip) => (tip.penalty ? (room[tip.penalty] ?? 50) > 0 : tip.rule ? room[tip.rule] ?? true : true))
    .map((tip) => t(`waitingTable.tips.${tip.key}`, tip.penalty ? { n: room[tip.penalty] ?? 50 } : undefined));
  const [i, setI] = useState(() => Math.floor(Math.random() * TIPS.length));
  useEffect(() => {
    const id = setInterval(() => setI((n) => n + 1), TIP_EVERY_MS);
    return () => clearInterval(id);
  }, [i]);
  const on = i % tips.length;
  return (
    <button
      type="button"
      data-testid="tips"
      onClick={() => setI((n) => n + 1)}
      className="w-full rounded-xl border border-amber-400/10 bg-amber-400/[0.04] px-2.5 py-2 text-start transition-colors hover:border-amber-400/25"
    >
      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-amber-400/80">
        <Lightbulb className="h-3 w-3" /> {t("waitingTable.tipTitle")}
      </span>
      <span className="mt-1 grid">
        {tips.map((tip, k) => (
          <span
            key={k}
            aria-hidden={k !== on}
            className={`[grid-area:1/1] text-[12px] leading-snug text-slate-300 transition-opacity duration-500 ${
              k === on ? "opacity-100" : "invisible opacity-0"
            }`}
          >
            {tip}
          </span>
        ))}
      </span>
    </button>
  );
}

/** The room's title, with dots while seats are free. */
export function WaitingTitle({ title, searching, className = "text-xl" }) {
  return (
    <h1 className={`font-display text-white ${className}`} aria-live="polite">
      {title}
      {searching && (
        <span aria-hidden="true" className="inline-flex w-5 justify-start">
          {[0, 1, 2].map((d) => (
            <motion.span
              key={d}
              animate={{ opacity: [0.2, 1, 0.2] }}
              transition={{ duration: 1.4, repeat: Infinity, delay: d * 0.2 }}
            >
              .
            </motion.span>
          ))}
        </span>
      )}
    </h1>
  );
}

/** The way out of both waiting rooms, the same in each: a round glass key with a lit edge. On hover
 *  its rim turns gold and the arrow leans towards the way out; it sinks when pressed. Its name is the
 *  tooltip: the top bar also holds the title and the code, no room for the word on a phone. */
export function LeaveButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="leave-room-btn"
      title={t("common.quit")}
      aria-label={t("common.quit")}
      className="group relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.12] bg-gradient-to-b from-slate-700/70 to-slate-950/90 text-slate-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.12),inset_0_-2px_4px_rgba(0,0,0,0.35),0_4px_12px_rgba(0,0,0,0.45)] transition-[transform,border-color,color,box-shadow] duration-200 hover:border-amber-400/60 hover:text-amber-200 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.14),inset_0_-2px_4px_rgba(0,0,0,0.35),0_0_0_3px_rgba(251,191,36,0.10),0_4px_16px_rgba(0,0,0,0.5)] active:scale-90 active:shadow-[inset_0_2px_6px_rgba(0,0,0,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 motion-reduce:transition-none"
    >
      <ArrowLeft
        className="h-[18px] w-[18px] transition-transform duration-200 rtl:rotate-180 group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5 motion-reduce:transition-none"
        strokeWidth={2.25}
      />
    </button>
  );
}

/** The launch button: a spinner while waiting, Play when it can start. */
export function LaunchButton({ label, onClick, busy, live, children }) {
  return (
    <motion.button
      data-testid="start-game-btn"
      onClick={onClick}
      disabled={!onClick}
      whileTap={onClick ? { scale: 0.97 } : {}}
      className={`relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl py-2 text-[14px] font-bold leading-tight transition-colors duration-200 ${
        onClick
          ? "bg-amber-500 text-slate-950 hover:bg-amber-400 shadow-[0_0_14px_rgba(245,197,66,0.25),0_0_32px_rgba(245,197,66,0.08)]"
          : live
          ? "cursor-default border border-amber-400/40 bg-amber-500/10 text-amber-200"
          : "cursor-default border border-white/[0.06] bg-slate-800/60 text-slate-300"
      }`}
    >
      {children}
      <span className="relative flex items-center gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
        {label}
      </span>
    </motion.button>
  );
}
